import { Drawer, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import { makeStyles } from 'tss-react/mui';
import { lazy, Suspense, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import Header from 'web-library/stories/molecule/Header/Header';
import { IconName } from 'web-library/stories/atom/Icon/Icon';
import { useHTMLFontSize } from '../hooks/fontSize';
import { Role } from '../models';
import HeaderUserDetails from '../modules/profile/components/HeaderUserDetails';
import { selectIsUserLoggedIn } from '../store/appSlice';
import RouteKey from '../routes/key';
import { UserDetailsResponse } from '../api-refactored/user/useGetUserDetails';

import {typeTheme} from "web-library/theme/helper/tssHelper"
const MobileMenu = lazy(() => import('./menu/MobileMenu'));
const DesktopMenu = lazy(() => import('./menu/DesktopMenu'));
const Typography = lazy(() => import('web-library/stories/atom/Typography/Typography'));
const Box = lazy(() => import('web-library/stories/atom/Box/Box'));
const Divider = lazy(() => import('web-library/stories/atom/Divider/Divider'));
const IconButton = lazy(() => import('web-library/stories/atom/IconButton/IconButton'));
const RootFontControl = lazy(
  () => import('web-library/stories/molecule/RootFontControl/RootFontControl')
);

const useHeaderStyles = makeStyles()((theme) =>
({
  mobileLogo: {
     ...typeTheme(theme, 'body1bold'),
    color: theme.color.text.textHeader,
    marginLeft: theme.spacing(1),
  },
  menuCloseContainer: {
    // width: '100%',
    display: 'flex',
    justifyContent: 'flex-end',
    marginBottom: theme.spacing(1),
  },
  menuContainer: {},
  menuRootFont: {
    display: 'flex',
    justifyContent: 'center',
  },
}));

const { auth } = RouteKey.child;
const { resetPassword, setPassword } = RouteKey.auth.child;

const pathExcludeUserDetails = {
  [`/${auth}/${resetPassword}`]: true,
  [`/${auth}/${setPassword}`]: true,
};

export type BaseRole = 'CANDIDATE' | 'COORDINATOR' | 'ADMIN' | null;
export const useBaseRole = (loggedIn: boolean, isSuccess: boolean, roles?: string[]): BaseRole =>
  useMemo(() => {
    if (!loggedIn) {
      return null;
    }
    if (isSuccess) {
      if (roles?.includes(Role.candidate)) {
        return 'CANDIDATE';
      }
      if (roles?.includes(Role.coordinator)) {
        return 'COORDINATOR';
      }
      if (roles?.includes(Role.opsAdmin) || roles?.includes(Role.sysAdmin)) {
        return 'ADMIN';
      }
      return null;
    }
    return null;
  }, [isSuccess, loggedIn, roles]);

export interface UserDetailsData {
  data: UserDetailsResponse | undefined;
  isSuccess: boolean;
}

/**
 * Default header component for all the pages in root router.
 *
 * User details data is passed from parent component (App.tsx -> RootRouter.tsx -> DefaultHeader.tsx)
 * to avoid calling the user details API in this component. It is because calling the API in this
 * component will causing the "isLoading" (user details API in App.tsx) state changes and re-rendering
 * of App component (becoming infinite re-rendering loop) when user details API call is having error.
 *
 * It is decided to pass down the data instead of saving it in the redux store cos it is server data
 * and should not save it in the redux store.
 *
 * @param data data from user details API
 * @param isSuccess indicator whether user details API call is success
 */
const DefaultHeader = ({ data, isSuccess }: UserDetailsData) => {
  const history = useHistory();
  const location = useLocation();
  const theme = useTheme();
  const { classes } = useHeaderStyles();

  const loggedIn = useSelector(selectIsUserLoggedIn);

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const baseRole = useBaseRole(loggedIn, isSuccess, data?.roles);

  const excludeUserDetails = location.pathname in pathExcludeUserDetails;

  const { increaseFont, decreaseFont, isDisableIncrease, isDisableDecrease } = useHTMLFontSize();

  const notDesktop = useMediaQuery(theme.breakpoints.down('md'));

  // @ts-ignore
  return (<>
    <Header
      locationUsed="CP"
      isEmpty={!loggedIn}
      // @ts-ignore
      onClickSeabLogo={() => history.push('/')}
      isHideAnnouncement
      MenuItemsComponent={
        !notDesktop ? (
          <Suspense fallback={null}>
            <DesktopMenu baseRole={baseRole} />
          </Suspense>
        ) : (
          <Suspense fallback={null}>
            <Typography variant="body1" className={classes.mobileLogo}>
              Candidates Portal
            </Typography>
          </Suspense>
        )
      }
      UserDetailsComponent={!excludeUserDetails && <HeaderUserDetails />}
      isDisableDecreaseButton={isDisableDecrease}
      isDisableIncreaseButton={isDisableIncrease}
      onClickDecreaseFont={decreaseFont}
      onClickIncreaseFont={increaseFont}
      menuToggle={() => setMobileMenuOpen(!mobileMenuOpen)}
    />
    {notDesktop && mobileMenuOpen && (
      <Suspense fallback={null}>
        <Drawer
          elevation={0}
          anchor="left"
          open={notDesktop && mobileMenuOpen}
          onClose={() => setMobileMenuOpen(false)}
        >
          <Box className={classes.menuContainer}>
            <Box className={classes.menuCloseContainer}>
              <IconButton
                name={IconName.cross}
                color={theme.color.icon.iconDefault}
                ariaLabel="mobile menu"
                onClick={() => setMobileMenuOpen(false)}
                iconSize="L" />
            </Box>
            <MobileMenu
              baseRole={baseRole}
              setMobileMenuOpen={setMobileMenuOpen}
              mobileMenuOpen={mobileMenuOpen}
            />

            <Divider />

            <Box className={classes.menuRootFont}>
              <RootFontControl
                fontControlButtonGroupBackgroundColor="#B0CAE333"
                isDisableDecreaseButton={isDisableDecrease}
                isDisableIncreaseButton={isDisableIncrease}
                onClickDecreaseFont={decreaseFont}
                onClickIncreaseFont={increaseFont}
              />
            </Box>
          </Box>
        </Drawer>
      </Suspense>
    )}
  </>);
};

export default DefaultHeader;
